// src/pages/AlleDoorloopjesPage.js
// Publiek overzicht van alle doorloopjes, per thema, en daarna de waarom- en hoe-stukken, elk als
// kaart met afbeelding (in de stijl van de stapkaarten van de app) en een link naar de voorvertoning.
// Onder de intro een rij om naar elke groep te springen. Per groep staan zes kaarten vooraan (op een
// telefoon drie, zie PubliekeDoorloopjes.css); de rest klapt open met de knop eronder. Die kaarten
// staan wel in de pagina, met hun link, zodat zoekmachines ze lezen en volgen.
// Bestaat alleen als de publieke voorvertoningen aan staan (zie utils/publiekeDoorloopjes).
import React, { useRef, useState } from 'react';
import { flushSync } from 'react-dom';

import PubliekeKaart from '../components/PubliekeKaart';
import PubliekePagina from '../components/PubliekePagina';
import SEO from '../components/SEO';
import { ALLE_DOORLOOPJES_DESCRIPTION as BESCHRIJVING } from '../utils/seoText';
import { SOORTEN, overzichtsGroepen, vooraan } from '../utils/publiekOverzicht';
import { OVERZICHT_PAD, usePubliekeDoorloopjes } from '../utils/publiekeDoorloopjes';
import './PubliekeDoorloopjes.css';

function knopTekst(titel, soort, aantal) {
  const { meervoud } = SOORTEN[soort];
  if (soort !== 'doorloopje') return `Bekijk alle ${aantal} ${meervoud}`;
  if (titel === 'Overig') return `Bekijk alle ${aantal} overige ${meervoud}`;
  return `Bekijk alle ${aantal} ${meervoud} over ${titel}`;
}

// Op een telefoon staan er drie vooraan; heeft een groep er niet meer dan zes, dan is de knop alleen
// daar te zien. Na de klik krijgt de eerste kaart die erbij kwam de focus.
function Groep({ titel, soort, anker, items }) {
  const [open, setOpen] = useState(false);
  const lijst = useRef(null);

  const openKlappen = () => {
    const eersteVerborgen = [...lijst.current.children].find((li) => li.offsetParent === null);
    flushSync(() => setOpen(true));
    eersteVerborgen?.querySelector('a')?.focus();
  };

  return (
    <section id={anker} aria-labelledby={`${anker}-titel`} className={`publiek-thema${open ? ' is-open' : ''}`}>
      <h2 id={`${anker}-titel`}>{titel}</h2>
      <ul className="publiek-kaarten" ref={lijst}>
        {vooraan(items).map((d) => (
          <li key={d.slug}>
            <PubliekeKaart item={d} />
          </li>
        ))}
      </ul>
      {!open && items.length > 3 && (
        <button type="button" onClick={openKlappen} className={`btn-secondary publiek-meer${items.length <= 6 ? ' alleen-telefoon' : ''}`}>
          {knopTekst(titel, soort, items.length)}
        </button>
      )}
    </section>
  );
}

function AlleDoorloopjesPage() {
  const { loading, doorloopjes, fout } = usePubliekeDoorloopjes();

  return (
    <PubliekePagina naInloggen="/doorloopjes">
      {({ openRegister }) => {
        if (loading) return <p className="publiek-melding">Doorloopjes worden geladen...</p>;
        if (!doorloopjes)
          return (
            <div className="publiek-melding">
              <SEO title="Pagina niet gevonden | Doorloopjes" description="Deze pagina bestaat niet." noindex={!fout} />
              <h1>{fout ? 'De doorloopjes laden lukte niet' : 'Pagina niet gevonden'}</h1>
            </div>
          );
        const groepen = overzichtsGroepen(doorloopjes);
        return (
          <div className="doorloopje-detail-container">
            <SEO title="Alles voor je lespraktijk | Doorloopjes" description={BESCHRIJVING} url={OVERZICHT_PAD} type="website" />
            <h1>Alles voor je lespraktijk</h1>
            <p className="publiek-intro">
              {BESCHRIJVING} Bekijk per Doorloopje, Waarom en Hoe waar het over gaat en de eerste stap.
            </p>
            <nav className="publiek-sprongen" aria-label="Spring naar">
              {groepen.map(({ titel, anker }) => (
                <a key={anker} href={`#${anker}`}>
                  {titel}
                </a>
              ))}
            </nav>
            {groepen.map((groep) => (
              <Groep key={groep.anker} {...groep} />
            ))}
            <div className="example-cta-section">
              <div className="example-cta-content">
                <h3>Alle stappen van elk doorloopje zien?</h3>
                <p>Maak gratis een account. Dan kun je alle doorloopjes, de assistent en de kennisbank gebruiken.</p>
                <button onClick={openRegister} className="btn-primary">
                  Gratis registreren
                </button>
              </div>
            </div>
          </div>
        );
      }}
    </PubliekePagina>
  );
}

export default AlleDoorloopjesPage;
